import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, IconButton, MenuItem, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useUserDashboardSettings } from '../../../hooks/useUserDashboardSettings';
import { useGetAllTasks } from '../../../services/react-query/tasks';
import { TaskStatusFilter } from '../../../types/Task';
import { UserConfigurableModule } from '../../../types/User';
import { Loading } from '../../../uikit';
import { containerStyles, controlButtonStyles, controlSectionStyles, iconButtonStyles, paperContainerStyles, tilesContainerStyles } from '../dashboardStyles';
import { TaskFooter } from './TaskFooter';
import { TaskTile } from './TaskTile';
import { useGetCurrentUserSettings } from '../../../services/react-query/users/useGetCurrentUserSettings';

interface ITasksPanelProps {
  currentEmployeeUuid?: string;
}

export function TasksPanel({ currentEmployeeUuid }: ITasksPanelProps) {
  const { dashboardSettings, updateSettings } = useUserDashboardSettings(UserConfigurableModule.TASKS);
  const [page, setPage] = useState<number>(1);
  const employee = currentEmployeeUuid && dashboardSettings.ownership === 'my' ? currentEmployeeUuid : undefined;
  const { data: currentUserSettings } = useGetCurrentUserSettings();
  const { data, isLoading } = useGetAllTasks({
    params: {
      page,
      per_page: 6,
      filters: {
        employee: employee ? [employee] : undefined,
        task_status: [dashboardSettings.filters?.status || 'overdue'],
      },
    },
  });

  if (isLoading) return <Loading />;

  const maxPage = data?.meta?.total_pages;
  const setNewPage = (newPage: number) => {
    // Disable pagination if maxPage is unavailable.
    if (!maxPage) return;
    if (newPage < 1 || newPage > maxPage) return;
    setPage(newPage);
  };

  const controlSection = (
    <Box sx={controlSectionStyles}>
      {currentUserSettings?.settings.isAdmin ? (
        <ToggleButtonGroup
          exclusive
          value={dashboardSettings.ownership}
          onChange={(_event, nextVal) => nextVal && updateSettings({ ownership: nextVal })}
          size="small"
        >
          <ToggleButton value="my" disabled={!currentEmployeeUuid} sx={controlButtonStyles}>
            My Tasks
          </ToggleButton>
          <ToggleButton value="all" sx={controlButtonStyles}>
            All Tasks
          </ToggleButton>
        </ToggleButtonGroup>
      ) : (
        <Typography variant="h6" sx={{ height: '3rem', fontWeight: 600, pt: 1 }}>
          Tasks
        </Typography>
      )}
      <TextField
        select
        slotProps={{
          input: { sx: { ...controlButtonStyles } },
        }}
        margin="none"
        sx={{ maxWidth: 120 }}
        value={dashboardSettings.filters?.status || 'overdue'}
        onChange={(event) => updateSettings({ filters: { status: event.target.value as TaskStatusFilter } })}
      >
        <MenuItem value={TaskStatusFilter.ACTIVE}>Active</MenuItem>
        <MenuItem value={TaskStatusFilter.NOT_STARTED}>Not Started</MenuItem>
        <MenuItem value={TaskStatusFilter.READY}>Ready</MenuItem>
        <MenuItem value={TaskStatusFilter.IN_PROGRESS}>In Progress</MenuItem>
        <MenuItem value={TaskStatusFilter.ON_HOLD}>On Hold</MenuItem>
        <MenuItem value={TaskStatusFilter.OVERDUE}>Overdue</MenuItem>
        <MenuItem value={TaskStatusFilter.COMPLETED}>Completed</MenuItem>
      </TextField>
      <Box sx={{ whiteSpace: 'nowrap' }}>
        <IconButton sx={iconButtonStyles} onClick={() => setNewPage(page - 1)} disabled={page <= 1 || !maxPage}>
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton sx={iconButtonStyles} onClick={() => setNewPage(page + 1)} disabled={!maxPage || page >= maxPage}>
          <KeyboardArrowRight />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ ...containerStyles, width: '33%' }}>
      {/* Container for task tiles and footer */}
      <Box component={Paper} sx={paperContainerStyles}>
        {controlSection}
        {/* Container for 9 task tiles */}
        <Box sx={tilesContainerStyles}>{data?.data?.map((task) => <TaskTile key={task.uuid} task={task} currentEmployeeUuid={currentEmployeeUuid} />)}</Box>
        <TaskFooter employee={employee} />
      </Box>
    </Box>
  );
}
